import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Box,
  Avatar,
  TextField,
  InputAdornment,
  Stack,
  Pagination,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SEO from "../../../utils/SeoComponent";
import { ROUTES } from "../../../constants/routes";

const BlogList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]); // store original unfiltered data
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${DESCO_API_BASE}/blog/index`);
        setBlogs(response.data.data); // current display list
        setAllBlogs(response.data.data); // keep a backup of original list
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    fetchBlogs();
  }, []);

  // useEffect to handle dynamic filtering
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setBlogs(allBlogs); // reset to all blogs
    } else {
      const filtered = allBlogs.filter(
        (blog) =>
          blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          blog.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          blog.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setBlogs(filtered);
    }
  }, [searchTerm, allBlogs]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredBlogs = blogs.filter(
    (blog) =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const pageCount = Math.ceil((blogs?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAreas = Array.isArray(blogs)
    ? blogs.slice(startIndex, startIndex + itemsPerPage)
    : [];

  // Handle page change
  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <SEO slug={"blogs"} />
      <Container maxWidth="lg" sx={{ py: 8, marginTop: "100px" }}>
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 3,
              fontWeight: 700,
              background: "linear-gradient(45deg, #C10E14 30%, #F44336 90%)",

              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            DESCO Blog
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
            Insights and Updates from Dubai's Real Estate Experts
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              maxWidth: 600,
              mb: 4,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "& fieldset": {
                  borderColor: "#C10E14", // default red
                },
                "&:hover fieldset": {
                  borderColor: "#C10E14", // red on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#C10E14", // red on focus
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {searchTerm && blogs.length === 0 && (
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: "center", mt: 4 }}
          >
            No blogs found for "{searchTerm}"
          </Typography>
        )}

        <Grid container spacing={4}>
          {paginatedAreas.map((post) => (
            <Grid item xs={12} md={6} key={post.id}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transform: "translateY(0)",
                  transition: "transform 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-8px)",
                  },
                }}
                // onClick={() => navigate(`/blog/${post?.slug}`)}
                onClick={() => navigate(ROUTES.BLOG_DETAIL(post?.slug))}
              >
                <CardMedia
                  component="img"
                  height="240"
                  image={post.image}
                  alt={post.title}
                />
                <CardContent sx={{ p: 3 }}>
                  <Chip
                    label={post.type}
                    size="small"
                    sx={{
                      mb: 2,
                      background:
                        "linear-gradient(45deg, #C10E14 30%, #F44336 90%)",
                      color: "white",
                    }}
                  />
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ mb: 2, fontWeight: 700 }}
                  >
                    {post.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {post?.short_description}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar alt={post?.title} src="/api/placeholder/40/40" />
                    <Box>
                      <Typography variant="subtitle2">
                        {post?.user_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Intl.DateTimeFormat("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }).format(new Date(post?.created_at))}
                        · {post?.readTime}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        {pageCount > 1 && (
          <Stack spacing={2} sx={{ mt: 6, alignItems: "center" }}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={handlePageChange}
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 600,
                  "&.Mui-selected": {
                    backgroundColor: "#C10E14",
                    color: "white",
                  },
                },
              }}
            />
          </Stack>
        )}
      </Container>
    </>
  );
};

export default BlogList;
